import type { GridColDef } from '@mui/x-data-grid';

import { memo, useRef, forwardRef, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { TableVirtuoso } from 'react-virtuoso';
import dayjs from 'dayjs';

import {
  Box,
  Chip,
  Table,
  Button,
  TableRow,
  TableCell,
  TableHead,
  Typography,
  TableContainer,
  LinearProgress,
  Tooltip,
} from '@mui/material';

import { spacingUtil } from 'src/utils/spacing-util';
import { formatInspectorCityStateCountry } from 'src/utils/inspector-location';
import { formatAppointmentShortDateInZone } from 'src/utils/timezone';

import { Iconify } from 'src/components/iconify';
import { UserIcon } from 'src/components/user-icon';
import { AuthorizationsTooltip } from 'src/components/authorizations-tooltip';
import { InspectorDistanceCell } from './inspector-distance-cell';
import { InspectorTooltip } from './inspector-tooltip';
import { BusinessUnitCell } from './business-unit-cell';
import { ActivityStatus, InspectorDTO } from 'src/types/dto';
import { PreferrenceType } from 'src/types/personnelPreferrence';
import { AuthorizationDTO } from 'src/types/dto/authorization.dto';
import { IBusinessUnit } from 'src/types/business-unit';
import { AppointmentDTO } from 'src/types/dto/appointment.dto';
import type { CompetencyHierarchy } from 'src/utils/competency-hierarchy';

const isInspectorPreferred = (inspector: InspectorDTO): boolean => {
  if (inspector.preferenceStatus === PreferrenceType.PREFERRED) {
    return true;
  }
  const preferrences = inspector.preferrences;
  if (!preferrences) {
    return false;
  }
  if (Array.isArray(preferrences)) {
    return preferrences.some((preference) => preference.type === PreferrenceType.PREFERRED);
  }
  return preferrences.type === PreferrenceType.PREFERRED;
};

const InspectorTableScroller = forwardRef<HTMLDivElement, any>((props, ref) => (
  <TableContainer component={Box} {...props} ref={ref} sx={{ scrollbarGutter: 'stable' }} />
));

InspectorTableScroller.displayName = 'InspectorTableScroller';

const InspectorTable = ({ style, ...props }: any) => (
  <Table
    {...props}
    style={{
      ...style,
      borderCollapse: 'separate',
      tableLayout: 'fixed',
      width: '100%',
    }}
  />
);

const ROW_HEIGHT = 72;

type InspectorColumn = Omit<GridColDef, 'width' | 'maxWidth'> & {
  width?: string;
};

interface InspectorTableRowProps {
  row: InspectorDTO;
  columns: InspectorColumn[];
  selectedIds: number[];
  handleCellClick: (params: { row: InspectorDTO }) => void;
  onDateCellClick?: (event: React.MouseEvent<HTMLElement>, row: InspectorDTO) => void;
  onRowClick?: (row: any) => void;
  [key: string]: any;
}

function getEarliestAvailabilityLabel(row: InspectorDTO, siteTimezoneId?: string): string {
  const from = row.earliestAvailability?.from;
  if (!from) {
    return '-';
  }
  const tz = siteTimezoneId || row.timezoneId || 'UTC';
  return formatAppointmentShortDateInZone(from, tz) || '-';
}

const InspectorTableRow = memo(
  ({
    row,
    columns,
    selectedIds,
    handleCellClick,
    onDateCellClick,
    onRowClick,
    ...props
  }: InspectorTableRowProps) => {
    const isSelected = selectedIds.includes(row.id);

    return (
      <TableRow
        hover
        selected={isSelected}
        sx={{ cursor: 'default', height: ROW_HEIGHT, maxHeight: ROW_HEIGHT }}
        {...props}
      >
        {columns.map((column) => {
          const isDateColumn = column.field === 'earliestAvailability';
          return (
            <TableCell
              key={column.field}
              sx={{
                width: column.width,
                maxWidth: column.width,
                height: ROW_HEIGHT,
                maxHeight: ROW_HEIGHT,
                verticalAlign: 'middle',
              }}
              onClick={() => {
                if (isDateColumn) {
                  return;
                }
                handleCellClick({ row });
                onRowClick?.(row);
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  minWidth: 0,
                  height: ROW_HEIGHT,
                  maxHeight: ROW_HEIGHT,
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                {column.renderCell?.({ row } as any) || (row as any)[column.field]}
              </Box>
            </TableCell>
          );
        })}
      </TableRow>
    );
  }
);

InspectorTableRow.displayName = 'InspectorTableRow';

interface Props {
  rows: InspectorDTO[];
  siteLocation: string;
  siteTimezoneId?: string;
  selectedIds: number[];
  onDateCellClick: (event: React.MouseEvent<HTMLElement>, row: InspectorDTO) => void;
  onHoverInspector: (id: number | undefined) => void;
  showChanges?: boolean;
  onRowClick?: (row: any) => void;
  onEndReached?: () => void;
  isDeskService?: boolean;
  sortField?: 'competencies' | 'travelDistance' | null;
  sortDirection?: 'ASC' | 'DESC' | null;
  onSortChange?: (
    field: 'competencies' | 'travelDistance',
    direction: 'ASC' | 'DESC' | null,
  ) => void;
  loading?: boolean;
  distanceLoading?: boolean;
  accreditationBodyIds?: number[];
  activityCompetencyIds?: number[];
  competencyHierarchy?: CompetencyHierarchy;
  emptyMessage?: string;
}

const InspectorListInfiniteScrollComponent = ({
  rows,
  siteLocation,
  siteTimezoneId,
  selectedIds,
  onDateCellClick,
  onHoverInspector,
  onRowClick,
  showChanges = false,
  onEndReached,
  isDeskService = false,
  sortField = null,
  sortDirection = null,
  onSortChange,
  loading = false,
  distanceLoading = false,
  accreditationBodyIds,
  activityCompetencyIds,
  competencyHierarchy,
  emptyMessage,
}: Props) => {
  const { t } = useTranslation('activities');
  const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const sortableFields: ('competencies' | 'travelDistance')[] = [
    'competencies',
    'travelDistance',
  ];

  const handleSortClick = (field: 'competencies' | 'travelDistance') => {
    if (!onSortChange) {
      return;
    }
    const isActive = sortField === field;
    const nextDirection = isActive
      ? sortDirection === 'ASC'
        ? 'DESC'
        : sortDirection === 'DESC'
          ? null
          : 'ASC'
      : 'ASC';
    onSortChange(field, nextDirection);
  };

  const columns: InspectorColumn[] = useMemo(() => {
    const baseColumns: InspectorColumn[] = [
      {
        field: 'name',
        headerName: t('allocation.modal.grid.columns.name'),
        width: showChanges ? '20%' : '22%',
        renderCell: ({ row }: any) => {
          const recommendationMessage = row.lastAllocatedAt
            ? t('allocation.details.recommendedReason', {
                monthYear: dayjs(row.lastAllocatedAt).format('MMMM YYYY'),
              })
            : '';
          const preferredMessage = isInspectorPreferred(row)
            ? t('allocation.details.preferredReason')
            : '';
          const statusMessages = [recommendationMessage, preferredMessage].filter(Boolean);
          const tooltipTitle = statusMessages.join('\n');

          return (
            <InspectorTooltip inspector={row}>
              <Tooltip
                title={
                  tooltipTitle ? (
                    <Box sx={{ whiteSpace: 'pre-line' }}>{tooltipTitle}</Box>
                  ) : (
                    ''
                  )
                }
                enterDelay={400}
                placement="top"
                disableHoverListener={!tooltipTitle}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: spacingUtil(1.5),
                    overflow: 'hidden',
                    maxWidth: '100%',
                    minWidth: 0,
                    cursor: tooltipTitle ? 'default' : undefined,
                  }}
                >
                  <UserIcon id={row.id} name={row.name} type={row.type} size="mini" />
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      maxWidth: '100%',
                      minWidth: 0,
                      overflow: 'hidden',
                    }}
                  >
                    <Typography variant="body2" noWrap title={row.name}>
                      {row.name}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: '100%',
                      }}
                    >
                      {formatInspectorCityStateCountry(row)}
                    </Typography>
                    {statusMessages.map((message) => (
                      <Typography
                        key={message}
                        variant="caption"
                        color="primary"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          maxWidth: '100%',
                        }}
                      >
                        {message}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              </Tooltip>
            </InspectorTooltip>
          );
        },
      },
      {
        field: 'competencies',
        headerName: t('allocation.modal.grid.columns.competencies'),
        width: showChanges ? '22%' : '24%',
        renderCell: ({ row }: any) => (
          <AuthorizationsTooltip
            containerIdentifier={`suitable-inspector-auth-${row.id}`}
            accreditationBodyIds={accreditationBodyIds}
            authorizations={row.authorizations}
            activityCompetencyIds={activityCompetencyIds}
            competencyHierarchy={competencyHierarchy}
            wrapActiveWithInactiveTooltip
          />
        ),
      },
      {
        field: 'travelDistance',
        headerName: t('allocation.modal.grid.columns.distance'),
        width: showChanges ? '16%' : '18%',
        renderCell: ({ row }: any) => (
          <InspectorDistanceCell
            row={row}
            siteLocation={siteLocation}
            isDeskService={isDeskService}
            distanceLoading={distanceLoading}
            showDirections={false}
          />
        ),
      },
      {
        field: 'businessUnit',
        headerName: t('allocation.modal.grid.columns.businessUnit'),
        width: showChanges ? '12%' : '13%',
        renderCell: ({ row }: any) => <BusinessUnitCell inspector={row} />,
      },
      {
        field: 'earliestAvailability',
        headerName: '',
        width: showChanges ? '16%' : '22%',
        renderCell: ({ row }: any) => {
          const isSelected = selectedIds.includes(row.id);
          const label = getEarliestAvailabilityLabel(row, siteTimezoneId);

          return (
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              disabled={isSelected}
              onClick={(event) => {
                event.stopPropagation();
                if (!isSelected) {
                  onDateCellClick(event, row);
                }
              }}
              endIcon={
                <Iconify icon={isSelected ? 'eva:checkmark-fill' : 'eva:plus-fill'} width={16} />
              }
              sx={{
                borderRadius: 1.5,
                textTransform: 'none',
                fontWeight: 500,
                px: 1.25,
                py: 0.5,
                minWidth: 0,
                borderColor: 'divider',
                color: 'text.primary',
                '& .MuiButton-endIcon': { ml: 0.75 },
              }}
            >
              {label}
            </Button>
          );
        },
      },
    ];

    if (showChanges) {
      const lastColumn = baseColumns.pop();
      baseColumns.push({
        field: 'changes',
        headerName: t('allocation.modal.grid.columns.changes'),
        width: '8%',
        renderCell: ({ row }: any) => (
          <Chip label={row.changes} color="default" variant="soft" sx={{ borderRadius: '50%' }} />
        ),
      });
      baseColumns.push(lastColumn as InspectorColumn);
    }

    return baseColumns;
  }, [
    t,
    accreditationBodyIds,
    isDeskService,
    siteLocation,
    siteTimezoneId,
    showChanges,
    selectedIds,
    onDateCellClick,
    distanceLoading,
  ]);

  const handleCellClick = useCallback(
    (params: { row: InspectorDTO }) => {
      onHoverInspector(params.row.id);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => onHoverInspector(undefined), 2000);
    },
    [onHoverInspector]
  );

  const handleEndReached = useCallback(() => {
    onEndReached?.();
  }, [onEndReached]);

  const TableComponents = useMemo(
    () => ({
      Scroller: InspectorTableScroller,
      Table: InspectorTable,
      TableHead,
      TableRow: ({ item: row, ...props }: any) => (
        <InspectorTableRow
          row={row}
          columns={columns}
          selectedIds={selectedIds}
          handleCellClick={handleCellClick}
          onDateCellClick={onDateCellClick}
          onRowClick={onRowClick}
          {...props}
        />
      ),
    }),
    [columns, selectedIds, handleCellClick, onDateCellClick, onRowClick]
  );

  if (!rows.length && loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          gap: 1,
          mt: spacingUtil(2),
        }}
      >
        <LinearProgress sx={{ width: '30%' }} color="inherit" />
        <Typography variant="body2">{t('allocation.modal.loadingText')}</Typography>
      </Box>
    );
  }

  if (!rows.length && !loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          mt: spacingUtil(2),
        }}
      >
        <Typography variant="body1">{emptyMessage ?? t('allocation.modal.noResults')}</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        mt: spacingUtil(2),
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <TableVirtuoso
          data={rows}
          components={TableComponents as any}
          fixedItemHeight={ROW_HEIGHT}
          endReached={handleEndReached}
          style={{ height: '100%' }}
          fixedHeaderContent={() => (
            <TableRow sx={{ height: 40 }}>
              {columns.map((column) => {
                const isSortable = sortableFields.includes(
                  column.field as 'competencies' | 'travelDistance',
                );
                const isActiveSort = sortField === column.field;
                const sortIcon =
                  isActiveSort && sortDirection === 'ASC'
                    ? 'ic:round-arrow-upward'
                    : isActiveSort && sortDirection === 'DESC'
                      ? 'ic:round-arrow-downward'
                      : 'line-md:arrows-vertical';

                return (
                  <TableCell
                    key={column.field}
                    sx={{
                      width: column.width,
                      maxWidth: column.width,
                      '&:hover': { cursor: isSortable ? 'pointer' : 'default' },
                      px: 2,
                      py: 0.5,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                    }}
                    onClick={() => {
                      if (isSortable) {
                        handleSortClick(column.field as 'competencies' | 'travelDistance');
                      }
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.25,
                        maxWidth: '100%',
                        verticalAlign: 'middle',
                      }}
                    >
                      <Typography component="span" variant="body2" noWrap sx={{ minWidth: 0 }}>
                        {column.headerName}
                      </Typography>
                      {column.field === 'travelDistance' && (
                        <Tooltip
                          arrow
                          describeChild
                          slotProps={{ tooltip: { sx: { maxWidth: 320 } } }}
                          title={
                            <Box component="span" sx={{ display: 'block' }}>
                              <Box component="span" sx={{ display: 'block' }}>
                                {t('allocation.modal.distanceColumnInfo')}
                              </Box>
                              <Box component="span" sx={{ display: 'block', mt: 0.75 }}>
                                {t('allocation.modal.distanceSortInfo')}
                              </Box>
                            </Box>
                          }
                        >
                          <Box
                            component="span"
                            sx={{ display: 'inline-flex', flexShrink: 0, lineHeight: 0 }}
                            onClick={(event) => event.stopPropagation()}
                            aria-label={t('allocation.modal.distanceColumnInfo')}
                          >
                            <Iconify icon="eva:info-outline" width={16} sx={{ opacity: 0.6 }} />
                          </Box>
                        </Tooltip>
                      )}
                      {isSortable && (
                        <Iconify
                          icon={sortIcon}
                          width={16}
                          sx={{
                            flexShrink: 0,
                            opacity: isActiveSort ? 1 : 0.5,
                          }}
                        />
                      )}
                    </Box>
                  </TableCell>
                );
              })}
            </TableRow>
          )}
        />
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
          py: spacingUtil(2),
          height: 60,
          flexShrink: 0,
        }}
      >
        {loading && (
          <>
            <LinearProgress sx={{ width: '30%' }} color="inherit" />
            <Typography variant="body2">{t('allocation.modal.loadingText')}</Typography>
          </>
        )}
      </Box>
    </Box>
  );
};

export const InspectorListInfiniteScroll = memo(InspectorListInfiniteScrollComponent);
